import { v4 as uuidv4 } from 'uuid';
import { filterRecommendationsCIF } from 'common/utils/recommendations';

// Types de préconisation CIF SCPI pris en charge, et le produit de simulation associé
const productByType = {
    SCPIRendement: 'scpi',
    SCPIaCredit: 'scpi',
    SCPIDemembreeCIF: 'dismemberedScpi',
};

// Libellé produit utilisé dans le nom de la simulation, par type de préconisation
const labelByType = {
    SCPIRendement: 'SCPI Rendement',
    SCPIaCredit: 'SCPI à crédit',
    SCPIDemembreeCIF: 'SCPI Démembrée',
};

// Inverse de labelByType — dérivé pour éviter la désynchronisation
const typeByLabel = Object.fromEntries(Object.entries(labelByType).map(([type, label]) => [label, type]));

const contactByTarget = {
    client: 'Client Principal',
    partner: 'Conjoint',
    clientAndPartner: 'Cosouscription',
};

// Types de préconisation CIF gérés par le flux eSigne (LODEOM + SCPI)
export const CIF_ESIGNE_TYPES = ['LODEOMCIF', ...Object.keys(productByType)];

// Vrai si une enveloppe eSigne CIF existe dans l'une des colonnes du RIP (client, clientAndPartner, partner)
export const hasCifEsigneEnvelope = (rip) => [
    rip.recommendationsCIF,
    rip.recommendationsCIFClientAndPartner,
    rip.partner && rip.partner.recommendationsCIF,
].some((recommendations) => recommendations
    && CIF_ESIGNE_TYPES.some((type) => recommendations[type] && recommendations[type].idEnvelopeESigne));

// Champs d'opération de la preco qui se mappent 1:1 dans le data de la simulation scpi
const buildData = ({ subscriptionDate, duration, products, needFunding, funding }) => ({
    subscriptionDate,
    duration,
    products,
    needFunding: needFunding || false,
    funding: needFunding ? { type: 'depreciable', ...(funding || {}) } : null,
});

/**
 * Construit les payloads de simulation à créer à partir des préconisations CIF SCPI.
 * Chaque préconisation SCPI retenue donne une simulation complétée en attente de validation.
 */
const buildSimulationsFromRecommendations = ({ recommendationsCIF, target, ripId, initialCounters = {} }) => {
    const beneficiary = target;

    const contact = contactByTarget[target] || 'Client Principal';
    const counters = { ...initialCounters };

    return (recommendationsCIF || [])
        .filter(({ type }) => productByType[type])
        .map((recommendation) => {
            const { type } = recommendation;
            const product = productByType[type];
            const label = labelByType[type];

            counters[type] = (counters[type] || 0) + 1;

            const name = `Demande CIF ${label} - ${contact} (${counters[type]})`;

            return {
                id: uuidv4(),
                ripId,
                product,
                beneficiary,
                name,
                completed: true,
                approval: 'waiting',
                data: buildData(recommendation),
            };
        });
};

const getCifRecommendationsForTarget = (body) => {
    if ('partner' === body.target) {
        return body.partner && body.partner.recommendationsCIF;
    }

    if ('clientAndPartner' === body.target) {
        return body.recommendationsCIFClientAndPartner;
    }

    return body.recommendationsCIF;
};

const getInitialCounters = (simulations) => (
    (simulations || []).reduce((counters, { name }) => {
        if ('string' !== typeof name) { return counters; }

        const typeEntry = Object.entries(typeByLabel).find(([label]) => name.includes(label));
        const match = name.match(/\((\d+)\)$/);

        if (!typeEntry || !match) {
            return counters;
        }

        const [, type] = typeEntry;
        const counter = Number(match[1]);

        return {
            ...counters,
            [type]: Math.max(counters[type] || 0, counter),
        };
    }, {})
);

const saveRecommendationSimulations = async (body, database, id) => {
    const filteredRecommendations = getCifRecommendationsForTarget(body) || [];

    if ('cif' !== body.choicePreco || !Array.isArray(filteredRecommendations)) {
        return null;
    }

    const existingSimulations = await database.simulations.find({ ripId: id });

    const simulations = buildSimulationsFromRecommendations({
        recommendationsCIF: filteredRecommendations,
        target: body.target,
        ripId: id,
        initialCounters: getInitialCounters(existingSimulations),
    });

    await Promise.all(simulations.map(({ id: simulationId, ...data }) => database.simulations.update(simulationId, data)));

    return simulations.length > 0 ? simulations[0].id : null;
};

export const getTypeRecommendations = (rip, urlTarget) => {
    const scpiTypes = ['SCPIRendement', 'SCPIaCredit', 'SCPIDemembreeCIF'];
    let recommendations;
    if ('client' === urlTarget) {
        recommendations = rip.recommendationsCIF;
    } else if ('clientAndPartner' === urlTarget) {
        recommendations = rip.recommendationsCIFClientAndPartner;
    } else {
        recommendations = rip.partner && rip.partner.recommendationsCIF;
    }
    const recommendationsFiltered = filterRecommendationsCIF(recommendations);

    return Boolean(recommendationsFiltered && recommendationsFiltered.some(({ type }) => scpiTypes.includes(type)));
};

export default saveRecommendationSimulations;
